import React, { FC, useState } from 'react';
import {
  faMobile,
  faPencilAlt,
  faTrash,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { Status, User, UserType } from '../models/appUsersModels';
import { Table } from 'shared/components';
import { Button } from '@vadiun/react-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useVerifyAction } from '@vadiun/react-hooks-legacy';


const FILTER_TABS: { label: string; value: string | null }[] = [
  { label: 'Todos', value: null },
  { label: 'Particular', value: UserType.PARTICULAR },
  { label: 'Corporativo', value: UserType.CORPORATE },
  { label: 'Corp. Particular', value: UserType.CORPORATE_PARTICULAR },
];

interface AppUsersTableProps {
  users: User[];
  totalItems: number;
  rowsPerPage: number;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  activationAction: (user: User) => void;
  onSearchChange: (search: string | null) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
  onUserTypeChange: (type: string | null) => void;
  activeUserType: string | null;
  handlePageChange: (currentPage: number) => void;
  onDeleteUser: (id: string) => Promise<void>;
  loadingDeleteAppUser: boolean;
  reload: () => void;
}
const AppUsersTable: FC<AppUsersTableProps> = ({
  users,
  totalItems,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  activationAction,
  onSearchChange,
  onSortChange,
  onUserTypeChange,
  activeUserType,
  handlePageChange,
  onDeleteUser,
  loadingDeleteAppUser,
  reload
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const verifyAction = useVerifyAction();

  const handleSearchChange = (search: string | null) => {
    setSearchValue(search || '');
    onSearchChange(search || null);
  };

  const columns = [
    {
      name: 'fullname',
      label: 'Nombre y Apellido',
      options: {
        filter: true,
        customBodyRender: (value: any, { rowIndex: index }) => {
          const user = users[index];
          const company = user.company?.business_name;
          const isCorporate = user.user_type === UserType.CORPORATE || user.user_type === UserType.CORPORATE_PARTICULAR;
          return (
            <div className="flex flex-col gap-1">
              {isCorporate && company && (
                <p style={{
                  color: '#0270AB',
                  textShadow: '1px 1px 1px #61FFD0',
                  border: '1px solid #61FFD0',
                  width: 'fit-content',
                  padding: '0px 6px',
                  borderRadius: '4px',
                  fontSize: '10.5px',
                  margin: 0,
                  fontWeight: 500
                }}>
                  {company}
                </p>
              )}
              <span className="whitespace-nowrap">{value}</span>
            </div>
          );
        }
      }
    },
    {
      name: 'username',
      label: 'Correo',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return <div className="flex whitespace-nowrap">{value}</div>;
        }
      }
    },
    {
      name: 'createdAt',
      label: 'Fecha de creación',
      options: {
        filter: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          const [fecha, hora] = value.split(' ');
          return (
            <div className="flex ">
              <div className="flex text-blue-900 gap-4 bg-blueGray-50">
                <span className="text-blue-800 whitespace-nowrap">{fecha}</span>
                <span className="text-blue-700 whitespace-nowrap">{hora}</span>
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'status',
      label: 'Estado',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div className="flex justify-center">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  opacity: 0.9,
                  borderRadius: '0.375rem', // rounded-md
                  paddingLeft: '0.5rem', // px-2
                  paddingRight: '0.5rem', // px-2
                  ...(value === Status.VERIFIED
                    ? { color: 'rgb(0, 112, 56)', backgroundColor: '#f0fdf4' } // text-green-600 bg-green-50
                    : value === Status.PENDING
                    ? { color: '#ea580c', backgroundColor: '#fef2f2' } // text-orange-600 bg-red-50
                    : { color: '#991b1b', backgroundColor: '#fee2e2' }) // text-red-800 bg-red-100
                }}
              >
                {value === Status.VERIFIED
                  ? 'VERIFICADO'
                  : value === Status.PENDING
                  ? 'PENDIENTE'
                  : 'RECHAZADO'}
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'reservations',
      label: 'Cantidad de reservas',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div className="flex justify-center">
              <div className="flex bg-blue-50 text-blue-700 rounded-2xl p-2 justify-center">
                {value}
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'user_type',
      label: 'Tipo de usuario',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: UserType) => {
          const isCorporate = value === UserType.CORPORATE || value === UserType.CORPORATE_PARTICULAR;

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start' }}>
              <span style={{
                color: isCorporate ? '#065f46' : '#1d4ed8',
                backgroundColor: isCorporate ? '#ecfdf5' : '#eff6ff',
                border: `1px solid ${isCorporate ? '#a7f3d0' : '#bfdbfe'}`,
                borderRadius: '9999px',
                padding: '2px 10px',
                fontWeight: 600,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap'
              }}>
                {isCorporate ? 'Corporativo' : 'Particular'}
              </span>
            </div>
          );
        }
      }
    },
    {
      name: 'is_active',
      label: 'Activo - Inactivo',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div className="flex justify-center">
              <div
                className="flex justify-center  opacity-90 border-2 rounded-md px-2"
                style={
                  value
                    ? {
                        border: '1px solid rgb(0, 168, 8)',
                        color: 'rgb(0, 168, 8)',
                        boxShadow: '0px 0px 6px rgb(0, 168, 8)'
                      }
                    : {
                        border: '1px solid rgb(255, 0, 0)',
                        color: 'rgb(255, 0, 0)',
                        boxShadow: '0px 0px 6px rgb(255, 0, 0)'
                      }
                }
              >
                {value ? 'ACTIVO' : 'INACTIVO'}
              </div>
            </div>
          );
        }
      }
    },
    {
      name: 'id',
      label: 'Acciones',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id: number, { rowIndex: index }) => {
          const user = users[index];

          const handleDeleteClick = () => {
            verifyAction({
              title: '¡Estás por eliminar un usuario!',
              body: 'Esta acción eliminará un usuario de forma permanente. ¿Estás seguro?',
              onAccept: async () => {
                await onDeleteUser(user.id);
                reload();
              }
            });
          };

          return (
            <div key={user.id} className="flex gap-4">
              <Button
                onClick={() => activationAction(user)}
                variant="light"
                className="w-16 h-6"
                color="blue"
              >
                Activar
              </Button>
              <Link
                to={'/main/inventories/appUsers/' + id}
                className="inline-flex items-center"
              >
                <FontAwesomeIcon
                  size="lg"
                  color="dodgerblue"
                  icon={faPencilAlt}
                  className=" hover:text-blue-600"
                />
              </Link>
              <Button
                onClick={handleDeleteClick}
                variant="light"
                className="w-8 h-8 flex items-center justify-center"
                color="red"
                aria-label="Eliminar usuario"
                disabled={loadingDeleteAppUser}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  size="sm"
                  className="hover:text-red-600"
                />
              </Button>
            </div>
          );
        }
      }
    }
  ];

  const data = users.map((x) => ({
    ...x
  }));
  return (
    <div className="w-full">
      <div className="flex gap-2 mb-4">
        {FILTER_TABS.map((tab) => {
          const isActive = activeUserType === tab.value;
          return (
            <button
              key={tab.value ?? 'all'}
              onClick={() => onUserTypeChange(tab.value)}
              style={{
                padding: '6px 16px',
                borderRadius: '9999px',
                fontWeight: isActive ? 700 : 400,
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.15s',
                border: isActive ? '2px solid #1d4ed8' : '2px solid #e5e7eb',
                backgroundColor: isActive ? '#1d4ed8' : '#fff',
                color: isActive ? '#fff' : '#374151',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <Table
        options={{
          filter: false,
          download: false,
          searchOpen: searchValue !== '',
          searchText: searchValue,
          elevation: 0,
          selectableRows: 'none',
          print: false,
          serverSide: true,
          count: totalItems,
          rowsPerPage: rowsPerPage,
          onChangePage(page: number) {
            onChangePage(page + 1, rowsPerPage);
            handlePageChange(page + 1);
          },
          onChangeRowsPerPage(rowsPerPage: number) {
            onChangeRowsPerPage(rowsPerPage);
          },
          onSearchChange: handleSearchChange,
          onColumnSortChange: (
            changedColumn: string,
            direction: 'asc' | 'desc'
          ) => {
            onSortChange(direction, changedColumn);
          },
          searchProps: {
            placeholder: 'Buscar por nombre, apellido o empresa...'
          },
          textLabels: {
            body: {
              noMatch: searchValue
                ? `No se encontraron usuarios para "${searchValue}"`
                : 'No se han encontrado registros que coincidan con la búsqueda',
              toolTip: 'Ordenar',
              columnHeaderTooltip: (column: any) => `Orden para ${column.label}`
            },
            pagination: {
              next: 'Próxima',
              previous: 'Anterior',
              rowsPerPage: 'Registros por página:',
              displayRows: 'de',
              jumpToPage: 'Ir a la página'
            },
            toolbar: {
              search: 'Buscar',
              downloadCsv: 'Descargar',
              print: 'Imprimir',
              viewColumns: 'Ver columnas',
              filterTable: 'Filtrar tabla'
            },
            filter: {
              all: 'Todos',
              title: 'FILTROS',
              reset: 'LIMPIAR'
            },
            viewColumns: {
              title: 'Ver columnas',
              titleAria: 'Ver/Ocultar columnas'
            },
            selectedRows: {
              text: 'registro(s) seleccionado',
              delete: 'Borrar',
              deleteAria: 'Borrar columnas seleccionadas'
            }
          }
        }}
        title={
          <div className="flex gap-4 justify-start items-center">
            <div className="flex gap-1">
              <FontAwesomeIcon icon={faUsers} size="1x" />
              <FontAwesomeIcon icon={faMobile} size="1x" />
            </div>
            <div>Usuarios App</div>
          </div>
        }
        data={data}
        columns={columns}
      />
    </div>
  );
};

export default AppUsersTable;
